import {
    BridgePosition,
    Call,
    Card,
    CardId,
    CardPosition,
    Cards,
    ForeignBoardReviewData,
    ForeignBoardReviewPlayerData,
    GamePhase,
    SeatPosition,
    SharkGameResultsTravelerV2,
    Trick,
    levels,
} from '../app/types';
import { CardDeck } from '../utils/shark-initialHelper';
import {
    convertCalltoStrain,
    convertIntToBridgePosition,
    convertIntToCall,
    intToVuln,
    strToCardId,
} from './game-engine-helper';
import { convertStringToBid, convertSuitRank } from './shark-game-engine-helper';
import { defaultCards, defaultSeatData } from '../app/defaults';

interface S3Player {
    pn: string;
    pair_uuid?: string;
    boards?: S3Boards;
}

interface S3TrickCard {
    p: number;
    c: CardId;
    w: boolean;
}

interface S3Board {
    auction: string;
    dealer: number;
    eh: string[];
    ewtr: number;
    nh: string[];
    nstr: number;
    play: S3TrickCard[];
    sh: string[];
    vuln: number;
    wh: string[];
    traveler: {
        c: number;
        d: number;
    };
}

type S3Boards = Record<string, S3Board>;

export interface S3MyResults {
    boards: S3Boards;
    p1: S3Player;
    p2: S3Player;
    sumMP?: number; //Match Point total only accurate only after the game is  over
    rMP: number; //Match Point Rank, that one is only valid while in game , strata takes over after the game.
    ruMP: number; //Match Point % is only accurate after the game is  over
    ruIMP: number;
    rIMP: number;
    traveler?: SharkGameResultsTravelerV2[];
    username?: string;
    uuid: string;
    c: number;
    d: number;
}

export type S3BoardData = S3MyResults & {
    myresults?: S3MyResults;
    uuid: string;
    board?: S3Board;
};

export type S3TravelerResults = (SharkGameResultsTravelerV2 & {
    board: S3Board;
})[];

const findS3Board = (s3data: S3BoardData, bIndex: number): S3Board | undefined => {
    const board = s3data.myresults?.boards?.[bIndex] ?? s3data.p1?.boards?.[bIndex]  ?? s3data.boards?.[bIndex] ?? s3data.myresults?.p1?.boards?.[bIndex];
    if (board !== undefined) {
        return board;
    }
    const traveler = s3data.traveler?.find((item: { bi: number }) => item.bi === bIndex) as unknown as S3BoardData;
    if (traveler !== undefined) {
        return {
            ...traveler.board,
            traveler: { d: traveler.d, c: traveler.c },
        };
    }

    const travelerx = s3data.myresults.traveler?.find(
        (item: { bi: number }) => item.bi === bIndex,
    ) as unknown as S3BoardData;
    if(travelerx) {
        return {
            ...travelerx.board,
            traveler: { d: travelerx.d, c: travelerx.c },
        };
    }
};

export const convertS3toForeignBoardReviewData = (
    s3data: S3BoardData | null,
    bn: number,
    index: number,
): ForeignBoardReviewData => {
    const cards: Cards = CardDeck();
    const bIndex = bn - 1;
    const s3board: S3Board | S3BoardData | null = findS3Board(s3data, bIndex);
    if (s3board === undefined) {
        return {} as ForeignBoardReviewData;
    }

    //Assign all cards from hands
    s3board.nh.forEach((card) => {
        const c: Card = cards[strToCardId(card)];
        c.bridgePosition = BridgePosition.north;
        c.cardPosition = CardPosition.HAND;
    });
    s3board.eh.forEach((card) => {
        const c: Card = cards[strToCardId(card)];
        c.bridgePosition = BridgePosition.east;
        c.cardPosition = CardPosition.HAND;
    });
    s3board.sh.forEach((card) => {
        const c: Card = cards[strToCardId(card)];
        c.bridgePosition = BridgePosition.south;
        c.cardPosition = CardPosition.HAND;
    });
    s3board.wh.forEach((card) => {
        const c: Card = cards[strToCardId(card)];
        c.bridgePosition = BridgePosition.west;
        c.cardPosition = CardPosition.HAND;
    });

    //Add one player data
    const playerData: ForeignBoardReviewPlayerData = convertS3toForeignBoardReviewPlayerData(
        s3board,
        cards,
        s3data.myresults?.uuid ?? s3data.uuid ?? s3data.p1?.pair_uuid,
    );
    //Set the auction
    const contractCall: Call = convertIntToCall(s3board.traveler.c);
    const result: ForeignBoardReviewData = {
        cards: cards,
        seatData: {
            [SeatPosition.top]: {
                ...defaultSeatData,
                bridgePosition: BridgePosition.north,
                seatPosition: SeatPosition.top,
                isMe: false,
                isVisible: true,
            },
            [SeatPosition.right]: {
                ...defaultSeatData,
                bridgePosition: BridgePosition.east,
                seatPosition: SeatPosition.right,
                isMe: false,
                isVisible: true,
            },
            [SeatPosition.bottom]: {
                ...defaultSeatData,
                bridgePosition: BridgePosition.south,
                seatPosition: SeatPosition.bottom,
                isMe: true,
                isVisible: true,
            },
            [SeatPosition.left]: {
                ...defaultSeatData,
                bridgePosition: BridgePosition.west,
                seatPosition: SeatPosition.left,
                isMe: false,
                isVisible: true,
            },
        },
        contract: {
            call: contractCall,
            level: levels[contractCall[0] as unknown as number],
            strain: convertCalltoStrain(contractCall),
            stake: 1,
        },
        gamePhase: GamePhase.END,
        vulnerable: intToVuln(s3board.vuln),
        showAuctionBox: true,
        boardStats: [],
        myBoardStatKeys: {
            r: 0,
            s: 0,
            c: 0,
            d: 0,
            x: 0,
            l: 0,
        },
        dealer: convertIntToBridgePosition(s3board.dealer),
        declarer: convertIntToBridgePosition(s3board.traveler.d),
        //    winners: [],
        playerData: [playerData],
    };
    return result;
};

export const convertS3toForeignBoardReviewPlayerData = (
    s3board: S3Board,
    cards: Cards,
    uuid: string,
): ForeignBoardReviewPlayerData => {
    //Add one player data
    const playerData: ForeignBoardReviewPlayerData = {
        auction: [],
        tricks: [],
        winners: [],
        uuid: uuid,
        declarer: undefined,
    } as ForeignBoardReviewPlayerData;
    //Set the auction
    const auctionArray = s3board.auction.split(' ');
    auctionArray.forEach((call) => {
        playerData.auction.push(convertStringToBid(call.toLowerCase()));
    });

    //Set the trick history
    const s3Tricks: S3TrickCard[] = s3board.play;
    let playsCount = 0;
    let trickCount = 0;
    playerData.tricks = [];
    s3Tricks.forEach((card) => {
        playsCount++;
        trickCount = Math.ceil(playsCount / 4) - 1;
        const trick: Trick = playerData.tricks[trickCount] ?? { cardIds: [], winner: undefined };
        if (trick.cardIds.length === 0) {
            playerData.tricks.push(trick);
        }
        trick.cardIds.push(strToCardId(card.c));
        if (card.w) {
            trick.winner = convertIntToBridgePosition(card.p);
            //       playerData.winners.push(convertIntToBridgePosition(card.p));
        }
    });

    playerData.declarer = convertIntToBridgePosition(s3board.traveler?.d);
    return playerData;
};
